/*
 * Gourmet Easy PDP gallery navigation.
 *
 * Two things Dawn does not ship:
 *
 * 1. Prev/next controls over the main image. Dawn's "thumbnail" gallery
 *    layout shows one media at a time and expects you to use the rail, so
 *    there is no way to step through with the image itself.
 *
 * 2. A single-image lightbox. Dawn's product modal stacks every media item
 *    in one long scrolling column. This turns it into one large image with
 *    a thumbnail strip beneath and the same prev/next controls.
 *
 * Both drive Dawn's own code paths rather than reimplementing them --
 * setActiveMedia for the page gallery, and the modal's existing .active
 * class for the lightbox -- so deferred video, variant switching and the
 * gallery's own scroll handling keep working.
 */
(() => {
  const ARROW_LEFT =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M15 4 7 12l8 8" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const ARROW_RIGHT =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 4l8 8-8 8" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function makeArrow(dir, label) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ge-gallery-nav ge-gallery-nav--' + dir;
    btn.setAttribute('aria-label', label);
    btn.innerHTML = dir === 'prev' ? ARROW_LEFT : ARROW_RIGHT;
    return btn;
  }

  /* ---------- 1. Main gallery ---------- */

  function initGallery() {
    const gallery = document.querySelector('.product__media-wrapper media-gallery');
    if (!gallery || gallery.querySelector('.ge-gallery-nav')) return;
    /*
     * Not in the collection quick-add modal. Dawn re-runs this script when
     * it injects the product markup there, but quick-add.css shows only the
     * first media item, so stepping to the next one left a blank frame.
     * The modal is a buy shortcut with a "View full details" link; the
     * gallery belongs on the product page.
     */
    if (gallery.closest('quick-add-modal')) return;

    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    if (!viewer) return;

    const host = viewer.closest('slider-component') || viewer.parentElement;
    if (!host) return;
    host.classList.add('ge-gallery-host');

    const rail = gallery.querySelector('[id^="GalleryThumbnails"]');

    /*
     * Navigate by thumbnail, not by viewer item.
     *
     * Dawn's setActiveMedia ends with
     * `announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition)`
     * and never null-checks activeThumbnail, so stepping onto a viewer item
     * that has no thumbnail throws and the image never changes. Going
     * through the thumbnails guarantees one exists, and clicking the button
     * reuses the handler Dawn already bound to it.
     *
     * Thumbnails Dawn itself hides (variant images, when hide_variants is
     * on) are skipped; the ones our own More control has collapsed are not,
     * since those are ordinary images the shopper should still reach.
     */
    const steppable = () =>
      [...(rail ? rail.querySelectorAll('[data-target]') : [])].filter(
        (li) =>
          li.hasAttribute('data-ge-thumb-hidden') || getComputedStyle(li).display !== 'none'
      );

    const step = (delta) => {
      const items = steppable();
      if (items.length < 2) return;
      const activeId = (viewer.querySelector('[data-media-id].is-active') || {}).dataset?.mediaId;
      let i = items.findIndex((li) => li.dataset.target === activeId);
      if (i < 0) i = 0;
      const next = items[(i + delta + items.length) % items.length];
      const btn = next.querySelector('button');
      if (btn) btn.click();
    };

    const prev = makeArrow('prev', 'Previous image');
    const next = makeArrow('next', 'Next image');
    prev.addEventListener('click', () => step(-1));
    next.addEventListener('click', () => step(1));
    host.append(prev, next);
  }

  /* ---------- 2. Lightbox ---------- */

  function initModal() {
    const modal = document.querySelector('product-modal.product-media-modal');
    if (!modal || modal.querySelector('.ge-lightbox-thumbs')) return;

    const content = modal.querySelector('[role="document"]');
    if (!content) return;

    /*
     * Looked up on every use, never cached. product-info.js replaces this
     * element's innerHTML wholesale on each variant change, so an array
     * captured at init holds detached nodes the moment someone switches
     * colour: the arrows and the strip then toggle .active on orphans and
     * the visible image never moves.
     */
    const getMedia = () => [...content.querySelectorAll('[data-media-id]')];
    if (!getMedia().length) return;

    modal.classList.add('ge-lightbox');

    const syncStrip = (activeId) => {
      strip.querySelectorAll('button').forEach((b) => {
        const on = b.dataset.mediaId === activeId;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-current', on ? 'true' : 'false');
      });
    };

    const setActive = (el) => {
      getMedia().forEach((m) => m.classList.toggle('active', m === el));
      syncStrip(el.dataset.mediaId);
      /*
       * Video and 3D are deferred-media: their content only exists once
       * loadContent runs, so stepping onto one has to trigger it the same
       * way Dawn does when the modal opens.
       */
      if (el.nodeName === 'DEFERRED-MEDIA' && typeof el.loadContent === 'function') {
        el.loadContent();
      }
    };

    const step = (delta) => {
      const items = getMedia();
      if (!items.length) return;
      const current = items.find((m) => m.classList.contains('active')) || items[0];
      setActive(items[(items.indexOf(current) + delta + items.length) % items.length]);
    };

    const strip = document.createElement('div');
    strip.className = 'ge-lightbox-thumbs';
    strip.setAttribute('role', 'list');

    /*
     * Rebuilt, not built once, for the same reason. The signature check
     * keeps it cheap: deferred-media injects an iframe when a video loads,
     * which trips the childList observer below without changing the set.
     */
    let signature = '';
    const buildStrip = () => {
      const items = getMedia();
      const next = items.map((m) => m.dataset.mediaId).join(',');
      if (next === signature) return;
      signature = next;
      strip.textContent = '';
      items.forEach((m, i) => {
        /*
         * For a plain image the media element *is* the <img>; only video and
         * 3D wrap a poster image in a deferred-media element. Looking only
         * inside gave every image thumbnail an empty box.
         */
        const img = m.tagName === 'IMG' ? m : m.querySelector('img');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ge-lightbox-thumb';
        btn.dataset.mediaId = m.dataset.mediaId;
        btn.setAttribute('aria-label', 'Show image ' + (i + 1));
        /*
         * Built as elements rather than concatenated into innerHTML. The URL
         * comes from Shopify's own media, so this was not exploitable, but a
         * src with a quote in it would have broken out of the attribute and
         * produced malformed markup -- and string-built HTML is the habit
         * worth not having in a file that renders customer-facing content.
         */
        if (img) {
          const thumb = document.createElement('img');
          /*
           * A thumbnail-sized request, not the lightbox's own image. This
           * used to reuse img.currentSrc || img.src -- the modal's full image
           * at width=1445 -- for a button about 70px wide, so opening any
           * product page downloaded every product image at full size for the
           * strip: 1.06-1.18 MB extra per mobile view (2026-09-25 review).
           * Shopify's CDN resizes on request; 160 covers the thumb at 2x.
           */
          const full = img.currentSrc || img.src;
          let small = full;
          try {
            const u = new URL(full, window.location.href);
            u.searchParams.set('width', '160');
            u.searchParams.delete('height');
            small = u.toString();
          } catch (error) {
            small = full;
          }
          thumb.src = small;
          thumb.width = 160;
          thumb.decoding = 'async';
          thumb.alt = '';
          thumb.loading = 'lazy';
          btn.appendChild(thumb);
        } else {
          const play = document.createElement('span');
          play.className = 'ge-lightbox-thumb__play';
          play.setAttribute('aria-hidden', 'true');
          play.textContent = '▶';
          btn.appendChild(play);
        }
        strip.appendChild(btn);
      });
    };

    buildStrip();

    /*
     * Delegated on the strip and resolved from data-media-id, rather than a
     * listener per button closing over its element. Same reason as the
     * thumbnail rail's More control: anything that re-renders inside the
     * modal detaches the button the listener was bound to, and the control
     * silently stops responding.
     */
    strip.addEventListener('click', (event) => {
      const btn = event.target.closest('.ge-lightbox-thumb');
      if (!btn) return;
      event.preventDefault();
      const target = getMedia().find((m) => m.dataset.mediaId === btn.dataset.mediaId);
      if (target) setActive(target);
    });

    const prev = makeArrow('prev', 'Previous image');
    const next = makeArrow('next', 'Next image');
    prev.addEventListener('click', () => step(-1));
    next.addEventListener('click', () => step(1));

    content.after(strip);
    content.parentElement.append(prev, next);

    /*
     * Dawn sets .active itself when the modal opens, from whichever image
     * was clicked. Watching for that keeps the strip's highlight correct
     * without duplicating its opening logic.
     */
    new MutationObserver(() => {
      const active = getMedia().find((m) => m.classList.contains('active'));
      if (!active) return;
      syncStrip(active.dataset.mediaId);
      const onBtn = strip.querySelector('.is-active');
      if (onBtn) onBtn.scrollIntoView({ block: 'nearest', inline: 'center' });
    }).observe(content, { attributes: true, attributeFilter: ['class'], subtree: true });

    /*
     * The variant swap itself. The strip lives outside `content`, so it
     * survives the innerHTML replacement and has to be re-pointed at the
     * new media rather than re-created -- initModal refuses to run twice.
     */
    new MutationObserver(() => buildStrip()).observe(content, { childList: true, subtree: true });

    modal.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') step(-1);
      if (e.key === 'ArrowRight') step(1);
    });

    /*
     * Dawn's ModalDialog closes a media-modal on *any* mouse pointerup that
     * is not inside a deferred-media or product-model:
     *
     *   this.addEventListener('pointerup', (event) => {
     *     if (event.pointerType === 'mouse' &&
     *         !event.target.closest('deferred-media, product-model')) this.hide();
     *   });
     *
     * That suits its original lightbox, which was just a column of images to
     * scroll and dismiss. With controls inside, it means every click on an
     * arrow, a thumbnail or the image itself closes the modal instead.
     *
     * Capture phase on the modal, so it runs before that bubble listener and
     * stops the event reaching it. Only for the image and our own controls --
     * clicking the surrounding backdrop still closes, which is what a
     * lightbox should do. pointerup and click are separate events, so
     * stopping this one leaves our click handlers untouched.
     */
    modal.addEventListener(
      'pointerup',
      (event) => {
        if (
          event.target.closest(
            '.ge-lightbox-thumbs, .ge-gallery-nav, .product-media-modal__content'
          )
        ) {
          event.stopPropagation();
        }
      },
      true
    );
  }

  function init() {
    initGallery();
    initModal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
